import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

export interface UseInViewOptions {
  /** Margin around the root, e.g. '200px' to react before the element is visible. */
  rootMargin?: string;
  /** Stop observing once the element became visible for the first time. */
  once?: boolean;
  /**
   * Commit visibility changes synchronously. Components mounted in response then run their mount
   * lifecycle — including React's StrictMode double invocation in development — before any queued
   * microtask, which matters for third-party players that finish loading in a promise callback.
   */
  sync?: boolean;
}

/**
 * Tracks whether the referenced element is inside the viewport.
 *
 * The state deliberately starts as `false` on every render pass so server and client markup match;
 * it only flips inside an effect. When `IntersectionObserver` is unavailable the element is treated
 * as visible so the caller never blocks its content.
 */
export function useInView<T extends Element>(
  options?: UseInViewOptions,
): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);
  const rootMargin = options?.rootMargin;
  const once = options?.once;
  const sync = options?.sync;

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return undefined;
    }

    const element = ref.current;
    if (!element) {
      return undefined;
    }

    const setInView = sync
      ? (isIntersecting: boolean) => flushSync(() => setIsInView(isIntersecting))
      : setIsInView;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setInView(entry.isIntersecting);
          if (once && entry.isIntersecting) {
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin, once, sync]);

  return [ref, isInView];
}
